import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@akuna/ui";
import { addDays, monthBounds, parseIsoDate } from "@/lib/financial/calc";
import type { FinancialTitle } from "@/lib/financial/types";
import { cn } from "@/lib/utils";
import type { FormMode } from "./FinancialFormDialog";
import { isTitleLinked } from "./helpers";
import { Panel, PanelTitle, PillFilter } from "./ui";
import { useFinancialFormat } from "./useFinancialFormat";

type View = "week" | "month";
const HIGH_DAY = 8000;

interface Props {
  open: FinancialTitle[];
  today: string;
  onForm: (mode: FormMode) => void;
}

/** Agenda de contas a pagar: semana ou mês, navegando para frente e para trás. */
export function PaymentAgenda({ open, today, onForm }: Props) {
  const { t } = useTranslation("financial");
  const fmt = useFinancialFormat();
  const [view, setView] = useState<View>("week");
  const [offset, setOffset] = useState(0);

  const changeView = (v: View) => {
    setView(v);
    setOffset(0);
  };

  const days = useMemo(() => {
    let isos: (string | null)[];
    if (view === "week") {
      const start = addDays(today, offset * 7);
      isos = Array.from({ length: 7 }, (_, k) => addDays(start, k));
    } else {
      const base = parseIsoDate(today);
      const { from, to } = monthBounds(base.getFullYear(), base.getMonth() + offset);
      const total = parseIsoDate(to).getDate();
      const lead = parseIsoDate(from).getDay(); // domingo = 0, como o rótulo de dias da semana
      isos = [...Array.from({ length: lead }, () => null), ...Array.from({ length: total }, (_, k) => addDays(from, k))];
    }
    return isos.map((iso) => {
      const items = iso ? open.filter((x) => x.due_date === iso) : [];
      return { iso, items, total: items.reduce((a, x) => a + Number(x.amount), 0) };
    });
  }, [view, offset, today, open]);

  const real = days.filter((d): d is typeof d & { iso: string } => !!d.iso);
  const rangeTotal = real.reduce((a, d) => a + d.total, 0);
  const first = real[0].iso;
  const last = real[real.length - 1].iso;
  const monthLabel = (() => {
    const d = parseIsoDate(first);
    const month = fmt.monthLong(d.getMonth());
    return t("period.monthOfYear", { month: month.charAt(0).toUpperCase() + month.slice(1), year: d.getFullYear() });
  })();
  const subtitle =
    view === "week"
      ? t("agenda.subtitle", { from: fmt.dshort(first), to: fmt.dshort(last), year: last.slice(0, 4) })
      : t("agenda.subtitleMonth", { month: monthLabel });
  const dowLabels = t("dow.short", { returnObjects: true }) as string[];
  const maxItems = view === "week" ? 3 : 2;

  return (
    <Panel className="p-5">
      <PanelTitle
        title={t("agenda.title")}
        subtitle={subtitle}
        right={
          <>
            <PillFilter
              label={t("agenda.viewLabel")}
              value={view}
              onChange={changeView}
              options={[
                { value: "week", label: t("agenda.viewWeek") },
                { value: "month", label: t("agenda.viewMonth") },
              ]}
            />
            <div className="flex items-center gap-1">
              <Button size="icon-sm" variant="outline" aria-label={t(view === "week" ? "agenda.prevWeek" : "agenda.prevMonth")} onClick={() => setOffset((o) => o - 1)}>
                <ChevronLeft className="h-4 w-4" aria-hidden />
              </Button>
              <Button size="sm" variant="outline" onClick={() => setOffset(0)} disabled={offset === 0}>
                {t("agenda.today")}
              </Button>
              <Button size="icon-sm" variant="outline" aria-label={t(view === "week" ? "agenda.nextWeek" : "agenda.nextMonth")} onClick={() => setOffset((o) => o + 1)}>
                <ChevronRight className="h-4 w-4" aria-hidden />
              </Button>
            </div>
          </>
        }
      />

      {view === "month" ? (
        <div className="mt-4 hidden grid-cols-7 gap-2 sm:grid" aria-hidden>
          {dowLabels.map((d) => (
            <div key={d} className="px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{d}</div>
          ))}
        </div>
      ) : null}

      <div className={cn("grid gap-2.5", view === "week" ? "mt-4 grid-cols-2 sm:grid-cols-4 lg:grid-cols-7" : "mt-2 grid-cols-2 sm:grid-cols-7 sm:gap-2")}>
        {days.map((d, i) => {
          if (!d.iso) return <div key={`blank-${i}`} className="hidden sm:block" aria-hidden />;
          const iso = d.iso;
          const isToday = iso === today;
          return (
            <div
              key={iso}
              className={cn(
                "flex flex-col rounded-xl border p-3",
                view === "week" ? "min-h-[140px]" : "min-h-[104px] p-2",
                isToday ? "bg-primary/5" : d.total ? "bg-muted/40" : "bg-card",
                d.total > HIGH_DAY ? "border-state-danger/30" : "border-border",
              )}
            >
              {view === "week" ? (
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{fmt.dow(iso)}</div>
              ) : (
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground sm:hidden">{fmt.dow(iso)}</div>
              )}
              <div className="flex items-baseline gap-1">
                <span className="font-display text-lg font-extrabold leading-tight">{iso.slice(8, 10)}</span>
                {view === "week" ? <span className="text-xs font-semibold text-muted-foreground">{fmt.monthShort(Number(iso.slice(5, 7)) - 1)}</span> : null}
              </div>
              <div className={cn("mt-1 text-sm font-bold", d.total > HIGH_DAY ? "text-state-danger-foreground" : d.total ? "text-foreground" : "text-muted-foreground/50")}>
                {d.total ? fmt.brl0(d.total) : "—"}
              </div>
              <div className="mt-2 flex flex-col gap-1">
                {d.items.slice(0, maxItems).map((x) => (
                  <button
                    key={x.id}
                    type="button"
                    onClick={() => onForm({ type: "editTitle", title: x })}
                    disabled={isTitleLinked(x)}
                    className="rounded-md bg-card px-2 py-1.5 text-left shadow-sm hover:shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <div className="truncate text-xs font-medium">{x.counterparty_name}</div>
                    {view === "week" ? <div className="text-[11px] text-muted-foreground">{fmt.brl0(Number(x.amount))}</div> : null}
                  </button>
                ))}
                {d.items.length > maxItems ? <div className="pl-0.5 text-[11px] text-muted-foreground">{t("agenda.more", { count: d.items.length - maxItems })}</div> : null}
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}
